import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import StatCard from "../components/ui/StatCard";
import Badge from "../components/ui/Badge";
import styles from "./Dashboard.module.css";

const fmt = (n) => `KES ${Number(n ?? 0).toLocaleString("en-KE", { minimumFractionDigits: 0 })}`;

export default function Dashboard() {
  const { isAdmin, profile } = useAuth();
  const navigate = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [clients, setClients] = useState([]);
  const [staff,   setStaff]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile) load();
  }, [profile]);  // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      if (isAdmin) await loadAdmin();
      else await loadStaff();
    } finally {
      setLoading(false);
    }
  }

  async function loadAdmin() {
    // All clients
    const { data: allClients } = await supabase
      .from("clients")
      .select("*, consultant:employees(full_name)")
      .order("created_at", { ascending: false });

    const c = allClients ?? [];
    const totalRevenue    = c.reduce((s, x) => s + Number(x.total_amount),    0);
    const totalCollected  = c.reduce((s, x) => s + Number(x.amount_received), 0);
    const totalOutstanding= c.reduce((s, x) => s + Number(x.balance_due),     0);

    setStats({ totalClients: c.length, totalRevenue, totalCollected, totalOutstanding });
    setClients(c.slice(0, 8));

    // Per-staff breakdown
    const { data: employees } = await supabase
      .from("employees")
      .select("id, full_name, role, status")
      .eq("role", "staff");

    const staffRows = (employees ?? []).map((emp) => {
      const mine = c.filter((x) => x.consultant_id === emp.id);
      return {
        ...emp,
        clientCount:  mine.length,
        collected:    mine.reduce((s, x) => s + Number(x.amount_received), 0),
        outstanding:  mine.reduce((s, x) => s + Number(x.balance_due), 0),
      };
    });
    setStaff(staffRows);
  }

  async function loadStaff() {
    const { data: myClients } = await supabase
      .from("clients")
      .select("*")
      .eq("consultant_id", profile.id)
      .order("created_at", { ascending: false });

    const c = myClients ?? [];
    const collected   = c.reduce((s, x) => s + Number(x.amount_received), 0);
    const outstanding = c.reduce((s, x) => s + Number(x.balance_due), 0);
    const thisMonth   = c.filter((x) => {
      const d = new Date(x.created_at);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });

    setStats({
      totalClients:  c.length,
      thisMonth:     thisMonth.length,
      collected,
      outstanding,
    });
    setClients(c.slice(0, 6));
  }

  if (loading) return <div className={styles.loading}>Loading dashboard…</div>;

  return (
    <div className={styles.page}>
      <div className={styles.welcome}>
        <h2>Welcome back, {profile?.full_name?.split(" ")[0]} 👋</h2>
        <p>{new Date().toLocaleDateString("en-KE", { weekday:"long", year:"numeric", month:"long", day:"numeric" })}</p>
      </div>

      {/* Stats */}
      <div className={styles.statsGrid}>
        {isAdmin ? (
          <>
            <StatCard label="Total Clients"      value={stats?.totalClients}               />
            <StatCard label="Total Revenue"      value={fmt(stats?.totalRevenue)}            />
            <StatCard label="Total Collected"    value={fmt(stats?.totalCollected)}  accent  />
            <StatCard label="Outstanding Balance" value={fmt(stats?.totalOutstanding)}       />
          </>
        ) : (
          <>
            <StatCard label="My Clients"         value={stats?.totalClients}               />
            <StatCard label="Added This Month"   value={stats?.thisMonth}                  />
            <StatCard label="Total Collected"    value={fmt(stats?.collected)}      accent  />
            <StatCard label="Outstanding"        value={fmt(stats?.outstanding)}           />
          </>
        )}
      </div>

      {/* Recent clients */}
      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <h3>{isAdmin ? "Recent Clients" : "My Clients"}</h3>
          <button className={styles.viewAll} onClick={() => navigate("/clients")}>
            View all →
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Service</th>
                {isAdmin && <th>Consultant</th>}
                <th>Balance Due</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/clients/${c.id}/edit`)} className={styles.row}>
                  <td><strong>{c.full_name}</strong><br/><span className={styles.phone}>{c.phone}</span></td>
                  <td>{c.service_type}</td>
                  {isAdmin && <td>{c.consultant?.full_name ?? "—"}</td>}
                  <td>{fmt(c.balance_due)}</td>
                  <td><Badge label={c.payment_status} /></td>
                  <td><Badge label={c.status} /></td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 5} className={styles.empty}>No clients yet.</td></tr>
              )}
            </tbody>
          </table>

          {/* Mobile Cards for Recent Clients */}
          <div className={styles.mobileList}>
            {clients.map((c) => (
              <div
                key={c.id}
                className={styles.clientCard}
                onClick={() => navigate(`/clients/${c.id}/edit`)}
              >
                <div className={styles.cardHeader}>
                  <div>
                    <div className={styles.cardName}>{c.full_name}</div>
                    <div className={styles.cardPhone}>{c.phone}</div>
                  </div>
                  <Badge label={c.status} />
                </div>

                <div className={styles.cardDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Service:</span>
                    <span className={styles.detailVal}>{c.service_type}</span>
                  </div>
                  {isAdmin && c.consultant?.full_name && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Consultant:</span>
                      <span className={styles.detailVal}>{c.consultant.full_name}</span>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Balance Due:</span>
                    <span className={`${styles.detailVal} ${Number(c.balance_due) > 0 ? styles.orange : styles.green}`}>
                      {fmt(c.balance_due)}
                    </span>
                  </div>
                </div>

                <div className={styles.cardFooter}>
                  <Badge label={c.payment_status} />
                  <span className={styles.cardActionHint}>Edit client →</span>
                </div>
              </div>
            ))}
            {clients.length === 0 && (
              <div className={styles.empty}>No clients yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Admin-only: per-staff table */}
      {isAdmin && staff.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHead}><h3>Staff Performance</h3></div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Consultant</th>
                  <th>Clients</th>
                  <th>Collected</th>
                  <th>Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => (
                  <tr key={s.id}>
                    <td><strong>{s.full_name}</strong></td>
                    <td>{s.clientCount}</td>
                    <td>{fmt(s.collected)}</td>
                    <td>{fmt(s.outstanding)}</td>
                    <td><Badge label={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Staff Cards */}
            <div className={styles.mobileList}>
              {staff.map((s) => (
                <div key={s.id} className={styles.staffCard}>
                  <div className={styles.staffHeader}>
                    <div className={styles.staffAvatar}>
                      {s.full_name?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div className={styles.staffInfo}>
                      <div className={styles.staffName}>{s.full_name}</div>
                      <div className={styles.staffClients}>{s.clientCount} clients assigned</div>
                    </div>
                    <Badge label={s.status} />
                  </div>
                  <div className={styles.staffMetrics}>
                    <div className={styles.staffMetric}>
                      <span className={styles.metricLabel}>Collected</span>
                      <span className={`${styles.metricVal} ${styles.green}`}>{fmt(s.collected)}</span>
                    </div>
                    <div className={styles.staffMetric}>
                      <span className={styles.metricLabel}>Outstanding</span>
                      <span className={`${styles.metricVal} ${Number(s.outstanding) > 0 ? styles.orange : ""}`}>
                        {fmt(s.outstanding)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
